import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ClipboardList, Plus, Loader2, Send, BarChart3, Edit, Trash2, Eye,
  CheckCircle, Clock, FileText, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => { fetchSurveys(); }, []);

  const fetchSurveys = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/surveys`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSurveys(res.data.surveys || []);
    } catch (err) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const deleteSurvey = async (id) => {
    if (!window.confirm('Umfrage wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/api/surveys/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Umfrage gelöscht');
      fetchSurveys();
    } catch (err) { toast.error('Fehler beim Löschen'); }
  };

  const resetResponses = async (id, title) => {
    if (!window.confirm(`Alle Antworten der Umfrage "${title}" zurücksetzen?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/surveys/${id}/responses`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(res.data.message);
      fetchSurveys();
    } catch (err) { toast.error('Fehler beim Zurücksetzen'); }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { label: 'Entwurf', icon: Clock, cls: 'bg-muted text-muted-foreground' },
      active: { label: 'Aktiv', icon: CheckCircle, cls: 'bg-green-500/20 text-green-600' },
      closed: { label: 'Geschlossen', icon: FileText, cls: 'bg-red-500/20 text-red-600' },
    };
    const s = map[status] || map.draft;
    const Icon = s.icon;
    return <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 ${s.cls}`}><Icon size={12} />{s.label}</span>;
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
              <ClipboardList className="text-primary" /> Umfragen
            </h1>
            <p className="text-muted-foreground mt-1">{surveys.length} Umfragen</p>
          </div>
          <Button onClick={() => navigate('/admin/surveys/new')} className="rounded-none uppercase tracking-wider" data-testid="create-survey-btn">
            <Plus className="w-4 h-4 mr-2" /> Neue Umfrage
          </Button>
        </div>

        {surveys.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border">
            <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Noch keine Umfragen erstellt.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {surveys.map(s => (
              <div key={s.id} className="bg-card border border-border p-4 flex items-center justify-between gap-4" data-testid={`survey-item-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold truncate">{s.title}</h3>
                    {statusBadge(s.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {s.questions?.length || 0} Fragen &middot; {s.responses_count || 0} Antworten
                    {s.sent_at && <> &middot; Gesendet: {new Date(s.sent_at).toLocaleDateString('de-CH')}</>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {s.status !== 'draft' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/surveys/${s.id}/results`)} title="Ergebnisse" data-testid={`survey-results-${s.id}`}>
                        <BarChart3 size={16} />
                      </Button>
                      {s.responses_count > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => resetResponses(s.id, s.title)} title="Antworten zurücksetzen" className="text-amber-500 hover:text-amber-600" data-testid={`survey-reset-${s.id}`}>
                          <RotateCcw size={16} />
                        </Button>
                      )}
                    </>
                  )}
                  {s.status !== 'draft' && (
                    <a href={`/public/survey/${s.id}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" title="Öffentliche Seite"><Eye size={16} /></Button>
                    </a>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/surveys/${s.id}/edit`)} title="Bearbeiten" data-testid={`survey-edit-${s.id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteSurvey(s.id)} title="Löschen" className="text-red-500 hover:text-red-600" data-testid={`survey-delete-${s.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
